/**
 * The Android app (android/, docs/android-app.md) shows the web app in a WebView. It adds
 * "OrticApp/<version>" to the user agent, and window.OrticApp is its bridge to the phone:
 *
 *   notify(json)                     a notification: { tag, title, body, url, channel } (url opens on tap)
 *   requestNotificationPermission()  asks Android for it (13 and later)
 *   takePending()                    what the phone handed the app (a share, a mailto: link, an .ics file),
 *                                    as JSON, once: { action: 'compose' | 'import-ics', to, cc, subject, body, files }
 *   sharedFile(index)                a file of it, as { name, type, data } with the bytes base64-encoded
 *   server()                         the server the app talks to
 *   changeServer()                   back to the app's server screen
 *   signIn(provider)                 signs in with 'google', 'microsoft' or 'github' in the phone's browser, whose
 *                                    sign-in pages refuse the WebView (OAuthController); the app then loads
 *                                    /oauth/app/finish (older versions of the app have no such call)
 *   credential()                     the device password its background check signs in with, as JSON
 *                                    { email, id }, or null; setCredential(json) hands it one ({ email, password,
 *                                    id }), clearCredential() takes it away; deviceName() names the phone
 *
 * The app tells the page of new pending content with the window event 'ortic-app-pending', opens a notification's
 * url with 'ortic-app-open' ({ detail: { url } }), and asks before going back with the cancelable 'ortic-app-back':
 * a view that closes something instead (the composer, a dialog) calls preventDefault().
 */

import { ref } from 'vue'

export const isNativeApp = typeof navigator !== 'undefined' && /\bOrticApp\/\d/.test(navigator.userAgent)

/** Content taken from the app (takeNativePending) until the view that handles it (mail, calendar) does. */
export const nativePending = ref(null)

function bridge() {
    return isNativeApp && typeof window !== 'undefined' ? window.OrticApp || null : null
}

export function nativeNotify({ tag, title, body, url, channel }) {
    bridge()?.notify(JSON.stringify({ tag: tag || '', title: title || '', body: body || '', url: url || '', channel: channel || 'mail' }))
}

export function nativeRequestNotificationPermission() {
    bridge()?.requestNotificationPermission()
}

/** What the phone handed the app, or null. Taking it clears it. */
export function takeNativePending() {
    const json = bridge()?.takePending()
    if (!json) return null
    try {
        return JSON.parse(json)
    } catch (e) {
        return null
    }
}

/** The files of a pending share as File objects. */
export function nativeSharedFiles(pending) {
    const files = []
    for (let index = 0; index < (pending?.files || []).length; index++) {
        const json = bridge()?.sharedFile(index)
        if (!json) continue
        const { name, type, data } = JSON.parse(json)
        const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0))
        files.push(new File([bytes], name, { type: type || 'application/octet-stream' }))
    }
    return files
}

export function nativeServer() {
    return bridge()?.server() || window.location.origin
}

export function nativeChangeServer() {
    bridge()?.changeServer()
}

/** Whether the app can sign in with Google, Microsoft or GitHub (in the phone's browser). */
export function nativeCanSignInOutside() {
    return typeof bridge()?.signIn === 'function'
}

export function nativeSignIn(provider) {
    bridge()?.signIn(provider)
}

/**
 * The background check's device password (BackgroundCheck in the app): Android freezes a closed app, and with it this
 * page's event stream, so the app asks the server itself every 15 minutes for new mail and reminders, signed in with
 * a device password of its own (as phones syncing calendars do). Made once per user and phone, listed and revocable
 * under "Connect your phone".
 */
export async function ensureNativeCredential(axios, user) {
    const app = bridge()
    if (!app || !user?.email) return
    const current = JSON.parse(app.credential() || 'null')
    if (current && current.email === user.email) return
    if (current?.id) {
        // Another user signed in on this phone before: the old password goes
        await axios.delete(`/api/dav/passwords/${current.id}`).catch(() => {})
    }
    const { data } = await axios.post('/api/dav/passwords', { name: `Ortic Mail (${app.deviceName()})` })
    app.setCredential(JSON.stringify({ email: user.email, password: data.password, id: data.id }))
}

/** Signing out: the background check stops and its password is revoked. */
export async function dropNativeCredential(axios) {
    const app = bridge()
    if (!app) return
    const current = JSON.parse(app.credential() || 'null')
    if (current?.id) await axios.delete(`/api/dav/passwords/${current.id}`).catch(() => {})
    app.clearCredential()
}
